/**
 * @file THE sync-source registry — one entry per source, in run order.
 *
 * A single entry here drives, on the client:
 *   • its stage in syncEverything() (array order IS the run order — Bamboo
 *     first because every device source resolves holders against /employees;
 *     materialise after all device sources; forms last)
 *   • its individual "sync just this one" button on Settings
 *   • its progress label on the shared Sync-everything button
 *   • its fragment in the success toast
 *   • its per-source diff in the manual-sync audit row (SYSTEM_SYNC_RAN —
 *     same shape the nightly cron emits, same audit-log renderer)
 *   • its status line on Settings (statusKey → /config/sync.{key})
 *
 * `materialiseAfter: true` marks stages whose output is only VISIBLE after
 * the join (raw mirrors + /employees feed materialise) — their individual
 * Settings buttons automatically chain a materialise run, because "sync one
 * source to verify a change" is meaningless if the app view doesn't update.
 *
 * Adding a source = ONE entry here… plus, server-side, its runner in
 * scheduledNightlySync (functions/sync-flexi-devices.js) and a
 * writeSyncStatus key — the cron is a separate bundle and cannot read this
 * file, so that half stays a two-line manual step.
 */

import {
  syncBambooHR, syncFlexiDevices, syncMerakiDevices, syncIruDevices,
  syncTestingDevices, materialiseDevices, syncFormResponses,
} from '../api.js';
import { formatDate } from '../utils.js';

export const SYNC_STAGES = [
  {
    key: 'bamboo',
    materialiseAfter: true,
    buttonLabel: 'BambooHR',
    progress: 'Syncing BambooHR...',
    run: syncBambooHR,
    status: { label: 'BambooHR', noun: 'employees' },
    toastFragment: (r) => `${r?.totalSeen ?? '?'} people`,
    auditDiff: (r) => ({
      totalSeen: r.totalSeen, added: r.added, updated: r.updated, errors: r.errors,
      newJoiners: r.newJoiners || [], phaseChanges: r.phaseChanges || [],
      stickyHeldOpen: r.stickyHeldOpen || [], rehires: r.rehires || [],
    }),
  },
  {
    key: 'flexi',
    materialiseAfter: true,
    buttonLabel: 'Abra Flexi',
    progress: 'Pulling Abra devices...',
    run: syncFlexiDevices,
    status: { label: 'Abra Flexi', noun: 'devices' },
    toastFragment: (r) => `${r?.totalSeen ?? '?'} Abra`,
    auditDiff: (r) => ({
      totalSeen: r.totalSeen, added: r.added, updated: r.updated,
      retiredCount: r.retiredCount, disposedCount: r.disposedCount, errors: r.errors,
      newDevices: r.newDevices || [], holderChanges: r.holderChanges || [], disposed: r.disposed || [],
    }),
  },
  {
    key: 'meraki',
    materialiseAfter: true,
    buttonLabel: 'Meraki',
    progress: 'Pulling Meraki MDM...',
    run: syncMerakiDevices,
    status: { label: 'Meraki MDM', noun: 'devices' },
    toastFragment: (r) => `${r?.totalSeen ?? 0} Meraki`,
    auditDiff: (r) => ({
      totalSeen: r.totalSeen, added: r.added, updated: r.updated,
      retiredCount: r.retiredCount, errors: r.errors,
      newDevices: r.newDevices || [], ownerChanges: r.ownerChanges || [],
    }),
  },
  {
    key: 'iru',
    materialiseAfter: true,
    buttonLabel: 'IRU',
    progress: 'Pulling IRU MDM...',
    run: syncIruDevices,
    status: { label: 'IRU MDM', noun: 'devices' },
    toastFragment: (r) => `${r?.totalSeen ?? 0} IRU`,
    auditDiff: (r) => ({
      totalSeen: r.totalSeen, added: r.added, updated: r.updated,
      retiredCount: r.retiredCount, errors: r.errors,
      newDevices: r.newDevices || [], ownerChanges: r.ownerChanges || [],
    }),
  },
  {
    key: 'testing',
    materialiseAfter: true,
    buttonLabel: 'Testing sheet',
    progress: 'Reading testing-devices sheet...',
    run: syncTestingDevices,
    status: null, // the testing sync writes no /config/sync entry
    toastFragment: (r) => `${r?.totalRows ?? 0} testing`,
    auditDiff: (r) => ({
      totalRows: r.totalRows, added: r.added, updated: r.updated,
      swept: r.swept, skipped: r.skipped, errors: r.errors,
      newDevices: r.newDevices || [],
    }),
  },
  {
    key: 'materialise',
    buttonLabel: 'Materialise',
    progress: 'Joining device records...',
    run: materialiseDevices,
    status: { label: 'Materialise (join)', line: (s) => (s?.lastSucceededAt
      ? `Materialise — last join ${formatDate(s.lastSucceededAt)}${s.summary?.errors ? `, ${s.summary.errors} errors` : ''}.`
      : 'Materialise — never run.') },
    // Flags needing admin attention after the join — worth calling out.
    toastFragment: (r) => {
      const flagged = (r?.holderMismatch ?? 0) + (r?.orphanNoAbra ?? 0)
                    + (r?.testingNotInAbra ?? 0) + (r?.testingWrongHolder ?? 0);
      return flagged ? `${flagged} flagged for reconcile` : null; // quiet when clean
    },
    auditDiff: (r) => ({
      matched: r.matched, holderMismatch: r.holderMismatch, abraUnassigned: r.abraUnassigned,
      orphanNoAbra: r.orphanNoAbra, decommissioned: r.decommissioned, decomStillInMdm: r.decomStillInMdm,
      abraOnlyNoMeraki: r.abraOnlyNoMeraki, sweptFlexi: r.sweptFlexi, sweptMeraki: r.sweptMeraki,
      sweptIru: r.sweptIru, sweptTesting: r.sweptTesting, testingMatched: r.testingMatched,
      testingWrongHolder: r.testingWrongHolder, testingNotInAbra: r.testingNotInAbra,
      gearAdopted: r.gearAdopted, errors: r.errors,
    }),
  },
  {
    key: 'forms',
    buttonLabel: 'Form responses',
    progress: 'Reading joining-form responses...',
    run: syncFormResponses,
    status: { label: 'Joining forms', noun: 'submissions' },
    toastFragment: (r) => `${r?.matched ?? 0} form matches${r?.unmatched ? ` (${r.unmatched} unmatched)` : ''}`,
    auditDiff: (r) => ({
      totalRows: r.totalRows, processed: r.processed, matched: r.matched,
      unmatched: r.unmatched, skipped: r.skipped, errors: r.errors,
    }),
  },
];

// ── Single-flight guard (per tab) ───────────────────────────────────────────
// Every sync entry point (Sync everything, each single-source button, the
// phone-tariffs fan-out) funnels through runExclusive, so only ONE sync can
// run per tab no matter which buttons are mashed. Buttons subscribe to
// onSyncBusyChange to grey themselves out while anything is in flight.
// Cross-tab and cron overlap are handled server-side by the materialise
// lease (functions/materialise-devices.js) — this guard is the UX half.
let syncBusy = false;
const busyListeners = new Set();

export function isSyncBusy() { return syncBusy; }

export function onSyncBusyChange(cb) {
  busyListeners.add(cb);
  return () => busyListeners.delete(cb);
}

function setSyncBusy(v) {
  syncBusy = v;
  for (const cb of busyListeners) {
    try { cb(v); } catch (err) { console.error('[sync] busy listener error', err); }
  }
}

/** Marker error for "another sync already holds the tab lock". */
export class SyncBusyError extends Error {
  constructor() { super('A sync is already running — wait for it to finish.'); this.name = 'SyncBusyError'; }
}

/** Run `fn` holding the tab-wide sync lock; throws SyncBusyError if held. */
export async function runExclusive(fn) {
  if (syncBusy) throw new SyncBusyError();
  setSyncBusy(true);
  try { return await fn(); } finally { setSyncBusy(false); }
}

/**
 * Run ONE stage (plus its chained materialise when flagged) under the tab
 * lock. Returns { result, ranStages } for the caller to toast/audit.
 * A chained-join failure lands in result.errors instead of throwing, so the
 * source's own success is still reported honestly.
 */
export async function runStage(stage, { onPhase } = {}) {
  return runExclusive(async () => {
    const result = { errors: [] };
    const ranStages = [stage];
    onPhase?.('sync');
    result[stage.key] = await stage.run();
    const mat = SYNC_STAGES.find((x) => x.key === 'materialise');
    if (stage.materialiseAfter && mat) {
      onPhase?.('join');
      try {
        result[mat.key] = await mat.run();
        ranStages.push(mat);
      } catch (err) {
        console.error('[sync] chained materialise failed', err);
        result.errors.push({ stage: 'materialise', error: err.message || String(err) });
      }
    }
    return { result, ranStages };
  });
}

/**
 * Run every stage in registry order, isolating failures per stage (one
 * source being down must not stop the rest — the nightly cron behaves the
 * same way). Returns `{ [stage.key]: result…, errors: [{stage, error}] }`.
 * `onProgress(key, 'start'|'success'|'error')` fires per stage, then
 * `('done', 'success'|'error')`.
 */
export async function syncEverything({ onProgress } = {}) {
  return runExclusive(async () => {
  const result = { errors: [] };
  for (const stage of SYNC_STAGES) {
    onProgress?.(stage.key, 'start');
    try {
      result[stage.key] = await stage.run();
      onProgress?.(stage.key, 'success');
    } catch (err) {
      result.errors.push({ stage: stage.key, error: err.message || String(err) });
      onProgress?.(stage.key, 'error');
    }
  }
  onProgress?.('done', result.errors.length ? 'error' : 'success');
  return result;
  });
}

/** The SYSTEM_SYNC_RAN audit `details` for a result — used by the manual
 *  Sync-everything button; single-source runs pass a one-stage subset. */
export function buildSyncAuditDetails(result, stages = SYNC_STAGES) {
  const details = { errors: result.errors || [] };
  for (const stage of stages) {
    const r = result[stage.key];
    if (r) details[stage.key] = stage.auditDiff(r);
  }
  return details;
}
